/*
 * SoleThreads — "Talk to the founder" feedback popup (snippets/solethreads-founder-feedback.liquid)
 *
 * <solethreads-founder-feedback> wraps one native <dialog> with Shopify's contact form.
 *   - Opened by the founder section's button ([aria-controls] = the dialog id). Closes on the ×
 *     button, a backdrop click, or Escape; the page behind does not scroll while it is open.
 *   - Fields are validated here (with the browser's own checks off) so messages are consistent.
 *   - The form posts to Shopify's /contact with fetch, so the page stays put. Success is shown only
 *     when Shopify confirms it (it redirects with ?contact_posted=true, or renders the form's
 *     posted_successfully? state); otherwise Shopify's own errors, or a retry message, are shown.
 *   - Shopify's spam protection (content_for_header) also handles this form's submit: it runs
 *     invisible hCaptcha, adds the token, then calls form.submit() — a native, page-leaving post.
 *     form.submit is pointed at send() so that final step posts with fetch instead, token included.
 *     Invalid or repeated submits are stopped before they reach Shopify's handlers.
 *   - If Shopify still answers with its bot challenge page, the form falls back to a normal submit
 *     so the visitor can complete it; Shopify then returns to this page and the popup reopens
 *     with the result.
 */
(() => {
  const TAG = "solethreads-founder-feedback";
  if (customElements.get(TAG)) return;

  const OPEN_CLASS = "st-ffb-open";
  // Deliberately simple: something@something.tld, no spaces.
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  // Shopify's captcha only logs when it fails or its challenge is closed; recover after this long.
  const CAPTCHA_TIMEOUT = 60000;
  const TEXT = {
    required: {
      name: "Please enter your name.",
      email: "Please enter your email address.",
      message: "Please write a message to the founder.",
    },
    invalidEmail: "Please enter a valid email address, like name@example.com.",
    fixErrors: "Please fix the highlighted fields.",
    sending: "Sending…",
    sendingStatus: "Sending your message.",
    sentStatus: "Your message has been sent.",
    failed: "We couldn't send your message. Please check your connection and try again.",
    captchaFailed: "We couldn't complete the spam check. Please try sending again.",
  };

  class SolethreadsFounderFeedback extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector("[data-ffb-dialog]");
      this.form = this.dialog && this.dialog.querySelector("form");
      if (!this.form) return;

      this.events = new AbortController();
      const { signal } = this.events;

      this.formView = this.form.querySelector("[data-ffb-form-view]");
      this.successView = this.form.querySelector("[data-ffb-success-view]");
      this.successTitle = this.form.querySelector("[data-ffb-success-title]");
      this.alert = this.form.querySelector("[data-ffb-alert]");
      this.status = this.form.querySelector("[data-ffb-status]");
      this.submitButton = this.form.querySelector("[data-ffb-submit]");
      this.submitLabel = this.form.querySelector("[data-ffb-submit-label]");
      this.inputs = [...this.form.querySelectorAll("[data-ffb-input]")];
      this.validated = false;
      this.sending = false;

      // Validation is handled here; without this script the browser's checks still apply.
      this.form.noValidate = true;

      // Set before Shopify's hCaptcha binds to the form: it keeps this as the submit to run once it
      // has a token, so the post goes through fetch instead of leaving the page.
      this.form.submit = () => this.send();

      document.querySelectorAll(`[aria-controls="${this.dialog.id}"]`).forEach((trigger) => {
        trigger.addEventListener("click", (event) => {
          event.preventDefault();
          this.open(trigger);
        }, { signal });
      });

      this.dialog.querySelectorAll("[data-ffb-close]").forEach((button) =>
        button.addEventListener("click", () => this.dialog.close(), { signal })
      );

      // The panel fills the dialog, so the dialog itself is only the target on the backdrop.
      // Both ends of the click must be there, so a text selection dragged outside doesn't close it.
      this.dialog.addEventListener("pointerdown", (event) => {
        this.pressedBackdrop = event.target === this.dialog;
      }, { signal });
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog && this.pressedBackdrop) this.dialog.close();
        this.pressedBackdrop = false;
      }, { signal });

      this.dialog.addEventListener("close", () => this.onClose(), { signal });
      this.form.addEventListener("submit", (event) => this.onSubmit(event), { signal });
      this.inputs.forEach((input) =>
        input.addEventListener("input", () => {
          if (this.validated) this.validate(input);
        }, { signal })
      );

      this.openForReturnedPost();
    }

    disconnectedCallback() {
      this.events?.abort();
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove(OPEN_CLASS);
    }

    // After a normal (non-script) submit, Shopify returns here with this form's success or errors.
    openForReturnedPost() {
      const returned =
        this.successView.hasAttribute("data-ffb-posted") || this.form.querySelector("[data-ffb-server-errors]");
      if (!returned) return;

      const formId = this.dataset.formId;
      const onlyContactForm = document.querySelectorAll('input[name="form_type"][value="contact"]').length === 1;
      if (window.location.hash === `#${formId}` || onlyContactForm) {
        this.open(document.querySelector(`[aria-controls="${this.dialog.id}"]`));
      }
    }

    open(opener) {
      if (this.dialog.open) return;
      this.opener = opener;

      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty("--st-ffb-scrollbar", `${Math.max(scrollbar, 0)}px`);
      document.documentElement.classList.add(OPEN_CLASS);

      if (typeof this.dialog.showModal === "function") this.dialog.showModal();
      else this.dialog.setAttribute("open", "");

      if (!this.successView.hidden) {
        this.successTitle.focus();
      } else if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
        // On touch screens, don't pop the keyboard up before the visitor has read the popup.
        this.inputs[0]?.focus();
      }
    }

    onClose() {
      document.documentElement.classList.remove(OPEN_CLASS);
      document.documentElement.style.removeProperty("--st-ffb-scrollbar");
      // A sent message has been seen: the next open starts a fresh form.
      if (!this.successView.hidden && !this.sending) this.showForm();
      this.opener?.focus({ preventScroll: true });
    }

    showForm() {
      this.successView.hidden = true;
      this.successView.removeAttribute("data-ffb-posted");
      this.formView.hidden = false;
      this.clearAlert();
    }

    showSuccess() {
      this.form.reset();
      this.inputs.forEach((input) => this.setError(input, ""));
      this.validated = false;
      this.clearAlert();
      this.formView.hidden = true;
      this.successView.hidden = false;
      this.status.textContent = TEXT.sentStatus;
      if (this.dialog.open) this.successTitle.focus();
    }

    // ----- Validation -----

    validate(input) {
      const value = input.value.trim();
      const field = input.dataset.ffbInput;
      let message = "";
      if (!value) message = TEXT.required[field];
      else if (field === "email" && (!EMAIL_PATTERN.test(value) || input.validity.typeMismatch)) {
        message = TEXT.invalidEmail;
      }
      this.setError(input, message);
      return !message;
    }

    setError(input, message) {
      const error = this.form.querySelector(`[data-ffb-error="${input.dataset.ffbInput}"]`);
      error.textContent = message;
      error.hidden = !message;
      if (message) {
        input.setAttribute("aria-invalid", "true");
        input.setAttribute("aria-describedby", error.id);
      } else {
        input.removeAttribute("aria-invalid");
        input.removeAttribute("aria-describedby");
      }
    }

    // ----- Submission -----

    onSubmit(event) {
      event.preventDefault();
      // Shopify's captcha handlers ignore preventDefault and post natively, so stop them here.
      if (this.sending) {
        event.stopImmediatePropagation();
        return;
      }

      this.validated = true;
      const invalid = this.inputs.filter((input) => !this.validate(input));
      if (invalid.length) {
        event.stopImmediatePropagation();
        this.showAlert(TEXT.fixErrors);
        invalid[0].focus();
        return;
      }

      this.clearAlert();
      this.setSending(true);

      if (this.form.dataset.hcaptchaBound || this.form.dataset.recaptchaBound) {
        // Shopify's captcha takes it from here and ends by calling form.submit(), i.e. send().
        this.captchaTimer = setTimeout(() => {
          if (this.posting) return;
          this.setSending(false);
          this.showAlert(TEXT.captchaFailed);
        }, CAPTCHA_TIMEOUT);
        return;
      }

      // No captcha on the form: post now. Shopify may still ask for its challenge (handled in send).
      event.stopImmediatePropagation();
      this.send();
    }

    async send() {
      clearTimeout(this.captchaTimer);
      if (this.posting) return;
      this.posting = true;
      this.clearAlert();
      this.setSending(true);

      try {
        const response = await fetch(this.form.action, {
          method: "POST",
          body: new URLSearchParams(new FormData(this.form)),
          headers: { Accept: "text/html" },
          credentials: "same-origin",
        });
        const url = new URL(response.url || window.location.href);

        // Shopify wants a human check: hand over to a normal submit so it can be completed.
        if (url.pathname.includes("/challenge")) {
          this.setSending(false);
          HTMLFormElement.prototype.submit.call(this.form);
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const html = await response.text();
        const page = new DOMParser().parseFromString(html, "text/html");
        const returnedForm = page.getElementById(this.dataset.formId);
        const posted =
          url.searchParams.get("contact_posted") === "true" ||
          Boolean(returnedForm?.querySelector("[data-ffb-posted]"));

        this.setSending(false);
        if (posted) {
          this.forgetSavedCopy();
          this.showSuccess();
          return;
        }

        // Not confirmed: show Shopify's own error text when it sent some.
        const serverErrors = returnedForm?.querySelector("[data-ffb-server-errors]")?.textContent.trim();
        this.showAlert(serverErrors || TEXT.failed);
      } catch (error) {
        console.warn("Founder feedback: submission failed.", error);
        this.setSending(false);
        this.showAlert(TEXT.failed);
      } finally {
        this.posting = false;
      }
    }

    // Shopify's captcha saves the form to sessionStorage (keyed by form_key) to refill it after a
    // page reload. Sent without one, so drop that copy of the message.
    forgetSavedCopy() {
      try {
        const key = this.form.elements.form_key?.value;
        if (key) window.sessionStorage.removeItem(key);
      } catch {}
    }

    setSending(sending) {
      this.sending = sending;
      this.submitButton.disabled = sending;
      this.submitButton.setAttribute("aria-busy", String(sending));
      this.submitLabel.textContent = sending ? TEXT.sending : this.submitLabel.dataset.label;
      this.inputs.forEach((input) => {
        input.readOnly = sending;
      });
      this.status.textContent = sending ? TEXT.sendingStatus : "";
    }

    showAlert(message) {
      this.alert.textContent = message;
      this.alert.hidden = false;
    }

    clearAlert() {
      this.alert.textContent = "";
      this.alert.hidden = true;
    }
  }

  customElements.define(TAG, SolethreadsFounderFeedback);
})();
